import { useEffect, useMemo, useState } from 'react';
import { Button } from '@vds/components';
import { getCookie, setCookie } from 'cookies-next';

import { regionToLocaleMap } from '@/config/locales';
import { DEV_LOCALE_COOKIE_KEY, DEV_LOCATION_COOKIE_KEY } from '@/utils/constants/common';
import { getSharedBranchCookieDomain } from '@/utils/domains';
import { getCurrentEnv } from '@/utils/getCurrentEnv';
import { getClientLOB } from '@/utils/server/lob-utils';

import styles from './SwitchTool.module.css';

const DEV_REGION_US = 'US';
const DEV_LOCALE_US = 'en-us';

const regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
const languageDisplayNames = new Intl.DisplayNames(['en'], { type: 'language' });

function getRegionName(regionCode: string): string {
    return regionDisplayNames.of(regionCode.toUpperCase()) ?? regionCode.toUpperCase();
}

function getLocaleGroupLabel(locale: string): string {
    return languageDisplayNames.of(locale) ?? locale;
}

type RegionEntry = { region: string; locale: string; displayName: string };
type RegionGroup = { locale: string; label: string; entries: RegionEntry[] };

const REGION_SELECT_ID = 'dev-region-select';

export default function RegionSwitch() {
    const [currentRegion, setCurrentRegion] = useState<string | null>();
    const [currentLocale, setCurrentLocale] = useState<string | null>();

    const lob = useMemo(() => getClientLOB(), []);
    const regionGroups = useMemo(() => {
        const localesByRegion = regionToLocaleMap[lob];
        if (!localesByRegion) {
            console.warn(`[RegionSwitch] Unknown LOB "${lob}", falling back to ptv`);
        }
        const resolvedLocales = localesByRegion ?? regionToLocaleMap.ptv;

        const groupMap = new Map<string, RegionGroup>();
        for (const [region, locale] of Object.entries(resolvedLocales)) {
            let group = groupMap.get(locale);
            if (!group) {
                group = { locale, label: getLocaleGroupLabel(locale), entries: [] };
                groupMap.set(locale, group);
            }
            group.entries.push({ region, locale, displayName: getRegionName(region) });
        }

        const groups = Array.from(groupMap.values());
        groups.sort((a, b) => a.label.localeCompare(b.label));
        for (const group of groups) {
            group.entries.sort((a, b) => a.displayName.localeCompare(b.displayName));
        }

        return groups;
    }, [lob]);

    const allEntries = useMemo(() => regionGroups.flatMap((g) => g.entries), [regionGroups]);

    const [selectedRegion, setSelectedRegion] = useState<string>(
        () =>
            allEntries.find((e) => e.region === DEV_REGION_US.toLowerCase())?.region ??
            allEntries[0]?.region ??
            DEV_REGION_US.toLowerCase(),
    );

    useEffect(() => {
        const region = (getCookie(DEV_LOCATION_COOKIE_KEY) as string) ?? DEV_REGION_US;
        const locale = (getCookie(DEV_LOCALE_COOKIE_KEY) as string) ?? DEV_LOCALE_US;
        setCurrentRegion(region);
        setCurrentLocale(locale);

        const normalizedRegion = region.toLowerCase();
        const normalizedLocale = locale.toLowerCase();

        const exactMatch = allEntries.find(
            (entry) => entry.region === normalizedRegion && entry.locale === normalizedLocale,
        );
        const regionOnlyMatch = allEntries.find((entry) => entry.region === normalizedRegion);

        if (exactMatch) {
            setSelectedRegion(exactMatch.region);
        } else if (regionOnlyMatch) {
            console.warn(
                `[RegionSwitch] Cookie locale "${locale}" does not match expected locale "${regionOnlyMatch.locale}" for region "${region}". Using region match.`,
            );
            setSelectedRegion(regionOnlyMatch.region);
        }
    }, [allEntries]);

    const handleRegionSwitch = (regionCode: string) => {
        const entry = allEntries.find((e) => e.region === regionCode);
        if (!entry) return;

        const env = getCurrentEnv();
        const sharedBranchDomain =
            env === 'platypus' ? getSharedBranchCookieDomain(window.location.hostname) : '';
        const regionSwitchCookieOpts = {
            path: '/',
            ...(sharedBranchDomain ? { domain: sharedBranchDomain } : {}),
        };
        setCookie(DEV_LOCATION_COOKIE_KEY, regionCode.toUpperCase(), regionSwitchCookieOpts);
        setCookie(DEV_LOCALE_COOKIE_KEY, entry.locale, regionSwitchCookieOpts);
        window.location.reload();
    };

    return (
        <div className={styles.infoContainer}>
            <h3>Region Switch Tool</h3>
            {currentRegion && (
                <div>
                    <b>Current Region:</b> <span>{currentRegion}</span>
                </div>
            )}
            {currentLocale && (
                <div>
                    <b>Current Locale:</b> <span>{currentLocale}</span>
                </div>
            )}
            <label htmlFor={REGION_SELECT_ID}>Switch Region / Locale:</label>
            <select
                id={REGION_SELECT_ID}
                className={styles.selectList}
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}>
                {regionGroups.map(({ locale, label, entries }) => (
                    <optgroup label={label} key={locale}>
                        {entries.map(({ region, displayName }) => (
                            <option value={region} key={region}>
                                {displayName} ({region.toUpperCase()})
                            </option>
                        ))}
                    </optgroup>
                ))}
            </select>
            <Button colorScheme="primary" onClick={() => handleRegionSwitch(selectedRegion)}>
                Switch
            </Button>
        </div>
    );
}
